import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import { buildQueryString } from "@/services/baseClient";
import type {
  ApiResponse,
  Session,
  CreateSessionRequest,
  UpdateSessionRequest,
  LiveStream,
  LiveStreamLatency,
  CreateLiveStreamRequest,
  UpdateLiveStreamRequest,
  PublicSessionStream,
  Lap,
  UpdateLapRequest,
  LapStatistics,
  SessionRecordingsResponse,
  RenameRecordingAngleResponse,
  RecordRecordingViewResponse,
  TeamLiveFocusState,
} from "@/types";

export function createSessionsApi({ client }: HttpClient) {
  async function getSessions(params?: {
    search?: string;
    scope?: "mine" | "all";
    userId?: string;
    trackId?: string;
    vehicleId?: string;
    eventId?: string;
    conductedAtFrom?: string;
    conductedAtTo?: string;
    includeLapSummary?: boolean;
    limit?: number;
    offset?: number;
    orderBy?: "createdAt" | "conductedAt" | "title";
    orderDirection?: "asc" | "desc";
  }): Promise<Session[]> {
    const queryString = buildQueryString({
      search: params?.search,
      scope: params?.scope,
      userId: params?.userId,
      trackId: params?.trackId,
      vehicleId: params?.vehicleId,
      eventId: params?.eventId,
      conductedAtFrom: params?.conductedAtFrom,
      conductedAtTo: params?.conductedAtTo,
      includeLapSummary: params?.includeLapSummary,
      limit: params?.limit,
      offset: params?.offset,
      orderBy: params?.orderBy,
      orderDirection: params?.orderDirection,
    });

    const response: AxiosResponse<ApiResponse<Session[]>> =
      await client.get(`/sessions${queryString}`);
    return response.data.data;
  }

  async function getLaps(sessionId: string): Promise<Lap[]> {
    const response: AxiosResponse<ApiResponse<Lap[]>> =
      await client.get(`/sessions/${sessionId}/laps`);
    return response.data.data;
  }

  return {
    getSessions,

    async getSession(id: string): Promise<Session> {
      const response: AxiosResponse<ApiResponse<Session>> =
        await client.get(`/sessions/${id}`);
      return response.data.data;
    },

    async getSessionRecordings(
      sessionId: string,
      options?: { includePlayback?: boolean },
    ): Promise<SessionRecordingsResponse> {
      const response: AxiosResponse<ApiResponse<SessionRecordingsResponse>> =
        options?.includePlayback === false
          ? await client.get(`/sessions/${sessionId}/recordings`, {
              params: { includePlayback: false },
            })
          : await client.get(`/sessions/${sessionId}/recordings`);
      return response.data.data;
    },

    // Renames the angle group `recordingId` belongs to (every recording in
    // the session sharing its current angleName). Throws on 409 with
    // `error.response.data.error.code === "ANGLE_NAME_TAKEN"` when another
    // angle in the session already uses the requested name.
    async renameRecordingAngle(
      recordingId: string,
      angleName: string,
    ): Promise<RenameRecordingAngleResponse> {
      const response: AxiosResponse<ApiResponse<RenameRecordingAngleResponse>> =
        await client.patch(`/recordings/${recordingId}/angle-name`, {
          angleName,
        });
      return response.data.data;
    },

    // Records one view of a recording (issue #649). Deduplicated per viewer
    // server-side. `token` is the signed playback token from the recording's
    // playbackUrl — required for anonymous viewers, ignored when the request
    // is authenticated.
    async recordRecordingView(
      recordingId: string,
      token?: string,
    ): Promise<RecordRecordingViewResponse> {
      const response: AxiosResponse<ApiResponse<RecordRecordingViewResponse>> =
        await client.post(`/recordings/${recordingId}/view`, { token });
      return response.data.data;
    },

    async createSession(
      sessionData: CreateSessionRequest,
    ): Promise<Session> {
      const response: AxiosResponse<ApiResponse<Session>> =
        await client.post("/sessions", sessionData);
      return response.data.data;
    },

    async updateSession(
      id: string,
      sessionData: UpdateSessionRequest,
    ): Promise<Session> {
      const response: AxiosResponse<ApiResponse<Session>> =
        await client.put(`/sessions/${id}`, sessionData);
      return response.data.data;
    },

    async deleteSession(id: string): Promise<void> {
      await client.delete(`/sessions/${id}`);
    },

    async getUserSessionsCount(): Promise<number> {
      const response: AxiosResponse<{
        success: boolean;
        data: { sessionsCount: number };
      }> = await client.get("/sessions/stats/count");
      return response.data.data.sessionsCount;
    },

    async getLiveStream(sessionId: string): Promise<LiveStream> {
      const response: AxiosResponse<ApiResponse<LiveStream>> =
        await client.get(`/sessions/${sessionId}/live-stream`);
      return response.data.data;
    },

    async getLiveStreamLatency(
      sessionId: string,
    ): Promise<LiveStreamLatency | null> {
      const response: AxiosResponse<ApiResponse<LiveStreamLatency | null>> =
        await client.get(`/sessions/${sessionId}/live-stream/latency`);
      // 202 Accepted with data: null while latency-rx has no samples yet.
      return response.data.data;
    },

    async getSessionStream(sessionId: string): Promise<PublicSessionStream> {
      const response: AxiosResponse<ApiResponse<PublicSessionStream>> =
        await client.get(`/sessions/${sessionId}/stream`);
      return response.data.data;
    },

    async getLiveStreamHistory(sessionId: string): Promise<LiveStream[]> {
      const response: AxiosResponse<ApiResponse<LiveStream[]>> =
        await client.get(`/sessions/${sessionId}/live-stream/history`);
      return response.data.data;
    },

    async createLiveStream(
      sessionId: string,
      data: CreateLiveStreamRequest,
    ): Promise<LiveStream> {
      const { clientRequestId, ...payload } = data;
      const response: AxiosResponse<ApiResponse<LiveStream>> =
        await client.post(
          `/sessions/${sessionId}/live-stream`,
          payload,
          clientRequestId
            ? { headers: { "Idempotency-Key": clientRequestId } }
            : undefined,
        );
      return response.data.data;
    },

    async updateLiveStream(
      sessionId: string,
      streamId: string,
      data: UpdateLiveStreamRequest,
    ): Promise<LiveStream> {
      const response: AxiosResponse<ApiResponse<LiveStream>> =
        await client.put(
          `/sessions/${sessionId}/live-stream/${streamId}`,
          data,
        );
      return response.data.data;
    },

    async deleteLiveStream(
      sessionId: string,
      streamId: string,
    ): Promise<void> {
      await client.delete(
        `/sessions/${sessionId}/live-stream/${streamId}`,
      );
    },

    async setLiveStreamRecording(
      sessionId: string,
      streamId: string,
      desired: boolean,
    ): Promise<LiveStream> {
      const response: AxiosResponse<ApiResponse<LiveStream>> =
        await client.put(
          `/sessions/${sessionId}/live-stream/${streamId}/recording`,
          { desired },
        );
      return response.data.data;
    },

    async focusLiveStream(
      sessionId: string,
      streamId: string,
      x: number,
      y: number,
    ): Promise<TeamLiveFocusState> {
      const response: AxiosResponse<ApiResponse<TeamLiveFocusState>> =
        await client.post(
          `/sessions/${sessionId}/live-stream/${streamId}/focus`,
          { x, y },
        );
      return response.data.data;
    },

    getLaps,

    async getLap(
      sessionId: string,
      lapId: string,
    ): Promise<Lap> {
      const response: AxiosResponse<ApiResponse<Lap>> =
        await client.get(`/sessions/${sessionId}/laps/${lapId}`);
      return response.data.data;
    },

    async updateLap(
      sessionId: string,
      lapId: string,
      lapData: Partial<UpdateLapRequest>,
    ): Promise<Lap> {
      const response: AxiosResponse<ApiResponse<Lap>> =
        await client.put(
          `/sessions/${sessionId}/laps/${lapId}`,
          lapData,
        );
      return response.data.data;
    },

    async deleteLap(
      sessionId: string,
      lapId: string,
    ): Promise<void> {
      await client.delete(`/sessions/${sessionId}/laps/${lapId}`);
    },

    async getLapStatistics(
      sessionId: string,
    ): Promise<LapStatistics> {
      const response: AxiosResponse<ApiResponse<LapStatistics>> =
        await client.get(`/sessions/${sessionId}/laps/statistics`);
      return response.data.data;
    },

    async getBestLap(sessionId: string): Promise<Lap | null> {
      const response: AxiosResponse<ApiResponse<Lap | null>> =
        await client.get(`/sessions/${sessionId}/laps/best`);
      return response.data.data;
    },
  };
}
